/**
 * Thin fetch wrapper that returns the generated response types.
 *
 * We use `types.gen.ts` for the schemas but bypass the @hey-api generated
 * service functions (their runtime client API drifted from the codegen version).
 */
import type {
  ArticleOut,
  CompanyDetailOut,
  CompanyListItemOut,
  EarningsCalendarItemOut,
  EarningsOut,
  HealthOut,
  ModelOut,
  NewsSourceOut,
  PredictionOut,
  PredictionRunOut,
  PriceSeriesOut,
} from "./generated";

type QueryValue = string | number | undefined | (string | number)[];

async function jsonGet<T>(path: string, params?: Record<string, QueryValue>): Promise<T> {
  const parts: string[] = [];
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === "") continue;
      if (Array.isArray(v)) {
        for (const item of v) {
          parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(item))}`);
        }
      } else {
        parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
      }
    }
  }
  const qs = parts.join("&");
  const url = qs ? `${path}?${qs}` : path;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

async function jsonPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export const api = {
  health: () => jsonGet<HealthOut>("/api/health"),
  listCompanies: (q?: string) =>
    jsonGet<CompanyListItemOut[]>("/api/companies", { q }),
  getCompany: (symbol: string) =>
    jsonGet<CompanyDetailOut>(`/api/companies/${encodeURIComponent(symbol)}`),
  setStar: (symbol: string, starred: boolean) =>
    jsonPut<CompanyDetailOut>(`/api/companies/${encodeURIComponent(symbol)}/star`, {
      starred,
    }),
  getPrices: (symbol: string, start?: string, end?: string) =>
    jsonGet<PriceSeriesOut>(`/api/companies/${encodeURIComponent(symbol)}/prices`, {
      start,
      end,
    }),
  listArticlesForCompany: (symbol: string, date: string) =>
    jsonGet<ArticleOut[]>(`/api/companies/${encodeURIComponent(symbol)}/articles`, {
      date,
    }),
  listEarnings: (symbol: string) =>
    jsonGet<EarningsOut[]>(`/api/companies/${encodeURIComponent(symbol)}/earnings`),
  listUpcomingEarnings: (limit?: number) =>
    jsonGet<EarningsCalendarItemOut[]>("/api/earnings/upcoming", { limit }),
  listEarningsByMonth: (year: number, month: number) =>
    jsonGet<EarningsCalendarItemOut[]>("/api/earnings/by-month", { year, month }),
  listArticlesForCompanyRange: (
    symbol: string,
    start: string,
    end: string,
    opts?: { sourceIds?: number[]; order?: "asc" | "desc" },
  ) =>
    jsonGet<ArticleOut[]>(`/api/companies/${encodeURIComponent(symbol)}/articles/range`, {
      start,
      end,
      source_ids: opts?.sourceIds && opts.sourceIds.length > 0 ? opts.sourceIds : undefined,
      order: opts?.order,
    }),
  listNewsSources: (q?: string) => jsonGet<NewsSourceOut[]>("/api/news-sources", { q }),
  listSourceArticles: (
    sourceId: number,
    opts?: { q?: string; companyId?: number },
  ) =>
    jsonGet<ArticleOut[]>(`/api/news-sources/${sourceId}/articles`, {
      q: opts?.q,
      company_id: opts?.companyId,
    }),
  listModels: () => jsonGet<ModelOut[]>("/api/models"),
  listModelRuns: (slug: string, date?: string) =>
    jsonGet<PredictionRunOut[]>(`/api/models/${encodeURIComponent(slug)}/runs`, { date }),
  listRunPredictions: (slug: string, runId: number) =>
    jsonGet<PredictionOut[]>(
      `/api/models/${encodeURIComponent(slug)}/runs/${runId}/predictions`,
    ),
};

export type {
  ArticleOut,
  CompanyDetailOut,
  EarningsCalendarItemOut,
  CompanyListItemOut,
  EarningsOut,
  HealthOut,
  ModelOut,
  NewsSourceOut,
  PredictionOut,
  PredictionRunOut,
  PriceSeriesOut,
};
